import React, { useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import { styled } from '@mui/material';

import { NoFiles } from 'components/organisms/Files';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import {
    Box,
    Button,
    Container,
    Drawer,
    Icon,
    IconButton,
    Link,
    ResourceChip,
    Stack,
    Text,
    TextInput,
} from 'shell/components';
import {
    downloadFileFromURL,
    getAbsoluteURL,
    getFileNameExt,
    getPrettyFileSize,
    getPrettyMIMEType,
    getResourceName,
    getS3FileURL,
    getTooltipComponent,
    getUserPreferredFullName,
} from 'utils/helper';

const renderResourceType = (resource) => {
    return resource.url ? 'Website Link' : getPrettyMIMEType(resource.data.type) || getFileNameExt(resource.data.name);
};

const renderResourceSize = (resource) => {
    if (resource.url) return '-';
    return resource.data.size ? getPrettyFileSize(resource.data.size, true) : 'Unknown';
};

const Mobile = ({ isLoading, resources, accountUsers, setUpload, filters, setFilters, numFilters, onDelete }) => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { instructorAccountUsers } = useUser2();

    const [drawer, setDrawer] = useState({ open: false, resource: null });

    const getResourceOptions = (resource) => {
        let options = [{ key: 'view', value: 'View Info' }];
        if (resource.data) options.push({ key: 'download', value: 'Download' });
        if (!resource.data) {
            options.push({ key: 'visit', value: 'Visit Link' });
            options.push({ key: 'copy', value: 'Copy Link' });
        }
        if (
            resource.owner_account_id === auth.owner_account_id ||
            !instructorAccountUsers.some(({ owner_account_id }) => owner_account_id === resource.owner_account_id)
        ) {
            options.push({ key: 'delete', value: 'Delete' });
        }
        return options;
    };

    const viewResource = (resource) => {
        setDrawer({ open: true, resource });
    };

    const handleSelect = ({ key }, resource) => {
        switch (key) {
            case 'view':
                viewResource(resource);
                break;
            case 'download':
                downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                break;
            case 'visit':
                downloadFileFromURL(getAbsoluteURL(resource.url));
                break;
            case 'copy':
                navigator.clipboard.writeText(getAbsoluteURL(resource.url));
                message.success('Link copied to clipboard.');
                break;
            case 'delete':
                onDelete(resource);
                break;
            default:
                break;
        }
    };

    const PageInner = (
        <CustomStack spacing={4}>
            <Stack direction={'row'} spacing={4}>
                <Button
                    color={'neutral'}
                    startIcon={<Icon kind={'filter'} />}
                    variant={'tertiary'}
                    onClick={() => setFilters((current) => ({ ...current, open: true }))}
                >
                    Filters {numFilters ? `(${numFilters})` : ''}
                </Button>
                <CustomTextInput
                    disablePointerEventsOnIcon={!Boolean(filters.searchInput)}
                    endIcon={
                        filters.searchInput ? (
                            <IconButton
                                aria-label={'clear search input'}
                                kind={'exit'}
                                variant={'square'}
                                onClick={() => setFilters((current) => ({ ...current, searchInput: '' }))}
                                onMouseDown={(e) => e.preventDefault()}
                            />
                        ) : (
                            <Icon kind={'search'} />
                        )
                    }
                    placeholder={'Search'}
                    value={filters.searchInput}
                    onChange={(e) => setFilters((current) => ({ ...current, searchInput: e.target.value }))}
                />
            </Stack>
            <Box sx={{ width: 'fit-content' }}>
                <Button
                    onClick={() => setUpload({ open: true })}
                    tooltip={{
                        component: getTooltipComponent(
                            'Public to Your Team',
                            'Upload files and links for your team to view.'
                        ),
                    }}
                >
                    Upload
                </Button>
            </Box>
            {isLoading ? (
                <CustomLogoLoading role={auth.role} />
            ) : (
                <>
                    {!resources.length ? (
                        <NoFiles />
                    ) : (
                        resources.map((resource) => (
                            <ResourceChip
                                key={resource.id}
                                options={getResourceOptions(resource)}
                                resource={resource}
                                size={'medium'}
                                type={resource.data ? 'file' : 'link'}
                                onClick={() => viewResource(resource)}
                                onSelect={handleSelect}
                            />
                        ))
                    )}
                </>
            )}
        </CustomStack>
    );

    return (
        <>
            {auth.role === 'instructor' ? PageInner : <CustomContainer device={'mobile'}>{PageInner}</CustomContainer>}
            <Drawer
                device={'mobile'}
                open={drawer.open}
                onClose={() => setDrawer((current) => ({ ...current, open: false }))}
            >
                {drawer.resource ? (
                    <Stack spacing={2}>
                        <IconButton
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => setDrawer((current) => ({ ...current, open: false }))}
                        />
                        <Text variant={'sh1'} weight={'semibold'}>
                            {getResourceName(drawer.resource)}
                        </Text>
                        <CustomBox>
                            <Text variant={'p2'}>Uploader</Text>
                            <Text variant={'p2'}>
                                {getUserPreferredFullName(
                                    accountUsers.find(
                                        ({ owner_account_id }) => owner_account_id === drawer.resource.owner_account_id
                                    )
                                )}
                            </Text>
                            <Text variant={'p2'}>Type</Text>
                            <Text variant={'p2'}>{renderResourceType(drawer.resource)}</Text>
                            {drawer.resource.data ? (
                                <>
                                    <Text variant={'p2'}>Size</Text>
                                    <Text variant={'p2'}>{renderResourceSize(drawer.resource)}</Text>
                                </>
                            ) : null}
                        </CustomBox>
                        {drawer.resource.data ? (
                            <CustomLink
                                external
                                href={getS3FileURL(drawer.resource.data)}
                                rel={'noopener noreferrer'}
                                target={'_blank'}
                            >
                                Open Document in New Tab
                            </CustomLink>
                        ) : (
                            <CustomLink
                                external
                                href={getAbsoluteURL(drawer.resource.url)}
                                rel={'noopener noreferrer'}
                                target={'_blank'}
                            >
                                Open Link in New Tab
                            </CustomLink>
                        )}
                    </Stack>
                ) : null}
            </Drawer>
        </>
    );
};

const CustomContainer = styled(Container)(() => {
    return {
        width: '100%',
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(8),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        flex: 1,
    };
});

const CustomLogoLoading = styled(LogoLoading)(({ role }) => {
    return {
        ...(role === 'instructor' && {
            height: '50vh',
        }),
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '40% 60%',
        gridGap: theme.spacing(2),
    };
});

const CustomLink = styled(Link)(({ theme }) => {
    return {
        marginTop: theme.spacing(4),
        color: theme.palette.ensightful.neutral[900],
        textDecoration: 'none',

        '&:hover': {
            textDecoration: 'underline',
        },
    };
});

export default Mobile;
